import { lazy, Suspense, type ReactNode, useEffect } from 'react';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from '@/components/error-boundary';
import { Toaster } from '@/components/ui/toaster';
import Home from '@/pages/Home';
import { StoreProvider } from '@/components/StoreContext';
import { Header } from '@/components/StoreShell';
import PwaExperience from '@/components/PwaExperience';
import { adminPath, isAdminPath } from '@/lib/admin-route';
import {
  Route,
  Switch,
  useLocation,
  Router as WouterRouter,
} from 'wouter';

const NotFound = lazy(() => import('@/pages/NotFound'));
const Brands = lazy(() => import('@/pages/Brands'));
const ProductPage = lazy(() => import('@/pages/Product'));
const BrandProductPage = lazy(() => import('@/pages/BrandProduct'));
const BrandCatalog = lazy(() => import('@/pages/BrandCatalog'));
const Sale = lazy(() => import('@/pages/Sale'));
const Cart = lazy(() => import('@/pages/Cart'));
const Checkout = lazy(() => import('@/pages/Checkout'));
const Admin = lazy(() => import('@/pages/Admin'));
const AdminBrands = lazy(() => import('@/pages/AdminBrands'));
const AdminProductReview = lazy(() => import('@/pages/AdminProductReview'));
const AdminPricingReview = lazy(() => import('@/pages/AdminPricingReview'));
const AdminNotifications = lazy(() => import('@/pages/AdminNotifications'));
const AdminSocialPosts = lazy(() => import('@/pages/AdminSocialPosts'));
const Terms = lazy(() => import('@/pages/Terms'));
const OurStory = lazy(() => import('@/pages/OurStory'));
const SearchPage = lazy(() => import('@/pages/Search'));
const Shop = lazy(() => import('@/pages/Shop'));

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError(error, query) {
      console.error("Storefront query failed", { queryKey: query.queryKey, error });
    },
  }),
  mutationCache: new MutationCache({
    onError(error, _variables, _context, mutation) {
      console.error("Storefront action failed", {
        mutationKey: mutation.options.mutationKey,
        error,
      });
    },
  }),
});

function Router() {
  const [location] = useLocation();
  const isAdminRoute = isAdminPath(location);
  const isControlRoomHome = isAdminRoute && location === adminPath();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  return (
    <RoutedErrorBoundary>
      <div className={`grain ${isControlRoomHome ? "" : "min-h-[100dvh]"} ${isAdminRoute ? "" : "storefront"}`}>
        {!isAdminRoute && <Header />}
        <Suspense fallback={<main className="min-h-[100dvh] bg-[#faf9f6] px-5 py-8 text-base text-[#685c53]" role="status">Loading page…</main>}>
          <Switch>
            <Route path={adminPath("/notifications")} component={AdminNotifications} />
            <Route path={adminPath("/social-posts")} component={AdminSocialPosts} />
            <Route path={adminPath("/brands")} component={AdminBrands} />
            <Route path={adminPath("/product-review")} component={AdminProductReview} />
            <Route path={adminPath("/pricing-review")} component={AdminPricingReview} />
            <Route path={adminPath()} component={Admin} />
            <Route path="/" component={Home} />
            <Route path="/brands" component={Brands} />
            <Route path="/search" component={SearchPage} />
            <Route path="/shop" component={Shop} />
            <Route path="/brands/ethnc/:category/:subfilter" component={BrandCatalog} />
            <Route path="/brands/ethnc/:category" component={BrandCatalog} />
            <Route path="/brands/ethnc" component={BrandCatalog} />
            <Route path="/brands/:brandSlug" component={BrandCatalog} />
            <Route path="/sale" component={Sale} />
            <Route path="/products/:handle" component={ProductPage} />
            <Route path="/brands/:brandSlug/products/:handle" component={BrandProductPage} />
            <Route path="/cart" component={Cart} />
            <Route path="/checkout" component={Checkout} />
            <Route path="/terms" component={Terms} />
            <Route path="/our-story" component={OurStory} />
            <Route component={NotFound} />
          </Switch>
        </Suspense>
      </div>
    </RoutedErrorBoundary>
  );
}

function RoutedErrorBoundary({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  return <ErrorBoundary resetKey={location}>{children}</ErrorBoundary>;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <StoreProvider>
        <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, '')}>
          <PwaExperience />
          <Router />
        </WouterRouter>
      </StoreProvider>
      <Toaster />
    </QueryClientProvider>
  );
}

export default App;
